import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { excludedKeys } from "../../config/constant.js";
import {
  fetchApplicationImg,
  fetchApplicationDataByIdAndName
} from "../../api/userService.js";
import { setSelectedApplicationIdData } from "../../store/ApplicationSlice.js";
import SideBarContent from "./SideBarContent.jsx";
import DetailSection from "./DetailSection.jsx";
import { Loader2, Building2, MapPin, Globe } from "lucide-react";

const CompanyAnalysisLayout = () => {
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [applicationData, setApplicationData] = useState({});
  const [error, setError] = useState(null);

  const dispatch = useDispatch();

  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData || {}
  );

  const company_details = applicationData?.analysis?.company_details;
  const uniqueValues = useSelector(state => state.intakeData.uniqueValues);
  const selectedApplicationIdInfo = useMemo(() => {
    return applicationData?.analysis;
  }, [applicationData]);
  const company = applicationData?.company_name;

  useEffect(() => {
    if (
      !selectedApplicationData?.application_id &&
      !selectedApplicationData?.customer_name
    ) {
      return;
    }

    const fetchApplicationData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchApplicationDataByIdAndName(
          selectedApplicationData?.customer_name || "",
          selectedApplicationData?.application_id
        );

        if (response) {
          setApplicationData(response);
          dispatch(setSelectedApplicationIdData(response));

          // Fetch logo if website exists
          if (response?.analysis?.company_details?.website) {
            await fetchImgData(response.analysis.company_details.website);
          }
        }
      } catch (err) {
        console.error("Error fetching application data:", err);
        setError(err.message || "Failed to fetch application data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationData();
  }, [selectedApplicationData, dispatch]);

  useEffect(() => {
    // Don't initialize map if still loading or no application data
    if (
      isLoading ||
      !applicationData ||
      Object.keys(applicationData).length === 0
    ) {
      return;
    }

    const latitudes =
      selectedApplicationIdInfo?.company_details?.latitudes || [];
    const longitudes =
      selectedApplicationIdInfo?.company_details?.longitudes || [];

    // Fallback to default location if no coordinates are available
    const defaultPosition = { lat: 37.7749, lng: -122.4194 };
    let mapCenter = defaultPosition;

    // Ensure we have valid lat/lng pairs
    if (latitudes.length > 0 && longitudes.length > 0) {
      mapCenter = {
        lat: Number(latitudes[0]), // Set initial center to first location
        lng: Number(longitudes[0])
      };
    }

    const loadGoogleMapsScript = () => {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAibBe9XsJ7hHfBuiihzcoSlbGp6P7IB8I&v=weekly`;
          script.defer = true;
          script.onload = resolve;
          script.onerror = () =>
            reject(new Error("Failed to load Google Maps script."));
          document.head.appendChild(script);
        }
      });
    };

    const initMap = async () => {
      // Add timeout to ensure DOM is ready
      const checkMapContainer = () => {
        return new Promise(resolve => {
          const check = () => {
            if (mapRef.current) {
              resolve();
            } else {
              setTimeout(check, 100); // Check every 100ms
            }
          };
          check();
        });
      };

      try {
        await checkMapContainer(); // Wait for map container to be available
      } catch (error) {
        console.error("Map container not found after waiting!");
        return;
      }

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: mapCenter, // Set map center
        zoom: 12,
        mapId: "15431d2b469f209e"
      });

      // Add multiple markers based on latitudes & longitudes
      if (latitudes.length > 0 && latitudes.length === longitudes.length) {
        latitudes.forEach((lat, index) => {
          const lng = longitudes[index];
          if (!lat || !lng) return;

          const marker = new window.google.maps.Marker({
            position: { lat: Number(lat), lng: Number(lng) },
            map: mapInstance,
            title: `Location ${index + 1}`
          });

          const streetViewService = new window.google.maps.StreetViewService();

          marker.addListener("click", () => {
            const latLng = new window.google.maps.LatLng(
              Number(lat),
              Number(lng)
            );

            streetViewService.getPanorama(
              { location: latLng, radius: 50 },
              (data, status) => {
                if (status === window.google.maps.StreetViewStatus.OK) {
                  const streetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
                  window.open(streetViewUrl, "_blank");
                } else {
                  const satelliteUrl = `https://www.google.com/maps/@${lat},${lng},20z/data=!3m1!1e3`;
                  window.open(satelliteUrl, "_blank");
                }
              }
            );
          });
        });
      }

      // Apply 3D view with initial tilt and heading
      window.google.maps.event.addListenerOnce(
        mapInstance,
        "tilesloaded",
        () => {
          mapInstance.setTilt(45);
          mapInstance.setHeading(20);
        }
      );

      // Initialize InfoWindow
      infoWindowRef.current = new window.google.maps.InfoWindow();
    };

    loadGoogleMapsScript()
      .then(initMap)
      .catch(error =>
        console.error("Failed to load Google Maps script:", error)
      );
  }, [selectedApplicationIdInfo, isLoading, applicationData]);

  const getWebsiteUrl = useCallback(url => {
    const website = url;
    if (!website) return ""; // Return empty string if website is undefined or null
    if (website.startsWith("https://") || website.startsWith("http://")) {
      return website;
    }
    return `https://${website}`;
  }, []);

  const fetchImgData = useCallback(
    async urlLink => {
      const url = getWebsiteUrl(urlLink);
      if (url) {
        const image = await fetchApplicationImg(url);
        if ("logo_url" in image) {
          setLogoUrl(image.logo_url);
        }
      }
    },
    [getWebsiteUrl]
  );

  useEffect(() => {
    if (company_details?.website) {
      fetchImgData(company_details.website);
    }
  }, [company_details?.website]);

  const handleCompanyChange = async companyName => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchApplicationDataByIdAndName(companyName);

      if (response) {
        setApplicationData(response);
        dispatch(setSelectedApplicationIdData(response));

        if (response?.analysis?.company_details?.website) {
          await fetchImgData(response.analysis.company_details.website);
        }
      }
    } catch (err) {
      console.error("Error fetching company data:", err);
      setError(err.message || "Failed to fetch company data");
    } finally {
      setIsLoading(false);
    }
  };

  const sections = selectedApplicationIdInfo
    ? Object.keys(selectedApplicationIdInfo).filter(
        key => !excludedKeys.includes(key)
      )
    : [];

  return (
    <div className="bg-gray-100 text-gray-800 mx-4">
      <div className="flex items-center space-x-4 my-2">
        <h1 className="text-2xl text-orange-600 font-semibold">
          Company Analysis
        </h1>
      </div>
      <div className="relative flex flex-col lg:flex-row gap-4">
        {error && <div className="text-red-500 p-4">{error}</div>}
        {isLoading ? (
          <div className="fixed inset-0 bg-gradient-to-br from-gray-900/50 to-gray-800/60 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
              {/* Animated loader */}
              <div className="relative mb-6">
                <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto" />
                <div className="absolute inset-0 w-16 h-16 border-4 border-orange-100 rounded-full mx-auto animate-pulse"></div>
              </div>

              {/* Main heading */}
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Analyzing Company Data
              </h2>

              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                We&apos;re gathering real-time insights and comprehensive
                company analysis for you...
              </p>

              {/* Feature indicators */}
              <div className="space-y-3">
                <div className="flex items-center justify-start gap-3 text-sm text-gray-700">
                  <Building2 className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span>Retrieving company profile & details</span>
                </div>
                <div className="flex items-center justify-start gap-3 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span>Loading location & map data</span>
                </div>
                <div className="flex items-center justify-start gap-3 text-sm text-gray-700">
                  <Globe className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span>Fetching digital presence information</span>
                </div>
              </div>

              {/* Progress indicator */}
              <div className="mt-6">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-400 to-orange-600 h-2 rounded-full animate-pulse"
                    style={{ width: "70%" }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  This may take a few moments...
                </p>
              </div>
            </div>
          </div>
        ) : (
          applicationData &&
          Object.keys(applicationData).length > 0 && (
            <>
              {/* Sidebar */}
              <SideBarContent
                company={company}
                fetchImgData={fetchImgData}
                uniqueValues={uniqueValues}
                setLogoUrl={setLogoUrl}
                logoUrl={logoUrl}
                applicationData={applicationData}
                getWebsiteUrl={getWebsiteUrl}
                mapRef={mapRef}
                onCompanyChange={handleCompanyChange}
              />

              {/* Main Content */}
              <DetailSection
                sections={sections}
                selectedApplicationIdInfo={selectedApplicationIdInfo}
              />
            </>
          )
        )}
      </div>
    </div>
  );
};

export default CompanyAnalysisLayout;
